/**
 * A ring of proportional slices, with the figures beside it as a list.
 *
 * The ring is decoration: every value it draws is in the legend as text, so
 * the SVG is hidden from assistive technology rather than given a label that
 * would name the chart without reporting anything it shows.
 *
 * @module components/ui/DonutChart
 */

import React from "react";

/**
 * Ring and swatch classes, paired so a slice's two marks always match.
 *
 * Literal strings, so Tailwind's scanner generates them; nothing here is
 * assembled from a prop and none of it needs the safelist.
 */
const SLICE_COLOURS = [
  { stroke: "stroke-primary", swatch: "bg-primary" },
  { stroke: "stroke-secondary", swatch: "bg-secondary" },
  { stroke: "stroke-accent", swatch: "bg-accent" },
  { stroke: "stroke-info", swatch: "bg-info" },
  { stroke: "stroke-success", swatch: "bg-success" },
  { stroke: "stroke-warning", swatch: "bg-warning" },
  { stroke: "stroke-error", swatch: "bg-error" },
  { stroke: "stroke-base-content", swatch: "bg-base-content" },
] as const;

export interface DonutSlice {
  /** Names the slice in the legend. Unique within one chart. */
  label: string;
  /** Share of the whole, 0 to 100. */
  percent: number;
}

export interface DonutChartProps {
  slices: readonly DonutSlice[];
  /** Shown in place of the chart when there is nothing to draw. */
  emptyMessage?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <DonutChart slices={[{ label: "Exam", percent: 60 }, { label: "Essay", percent: 40 }]} />
 * ```
 */
export function DonutChart({
  slices,
  emptyMessage = "Nothing recorded.",
  "data-testid": testId = undefined,
}: DonutChartProps): React.ReactElement {
  if (slices.length === 0) {
    return (
      <p className="text-sm text-muted" data-testid={testId}>
        {emptyMessage}
      </p>
    );
  }

  // Each slice starts where the ones before it end, so the ring reads round
  // once rather than every arc starting at twelve o'clock.
  const segments = slices.map((slice, index) => ({
    ...slice,
    offset: slices.slice(0, index).reduce((total, earlier) => total + earlier.percent, 0),
    colour: SLICE_COLOURS[index % SLICE_COLOURS.length]!,
  }));

  return (
    <div className="grid items-center gap-5 sm:grid-cols-[10rem_1fr]" data-testid={testId}>
      {/* `pathLength="100"` makes the dash array read as percentages whatever
          the radius, and the rotation starts the first slice at twelve
          o'clock rather than three. */}
      <svg className="mx-auto size-40" viewBox="0 0 100 100" aria-hidden focusable="false">
        <circle
          cx="50"
          cy="50"
          r="36"
          pathLength="100"
          fill="none"
          className="stroke-base-300"
          strokeWidth="20"
        />
        {segments.map((segment) => (
          <circle
            key={segment.label}
            cx="50"
            cy="50"
            r="36"
            pathLength="100"
            fill="none"
            className={segment.colour.stroke}
            strokeWidth="20"
            strokeDasharray={`${segment.percent} ${100 - segment.percent}`}
            strokeDashoffset={-segment.offset}
            transform="rotate(-90 50 50)"
          />
        ))}
      </svg>
      <ul className="space-y-2 text-sm">
        {segments.map((segment) => (
          <li key={segment.label} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className={`size-3 shrink-0 rounded-full ${segment.colour.swatch}`}
                aria-hidden
              />
              <span>{segment.label}</span>
            </span>
            <span className="font-semibold">{Math.round(segment.percent)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
